import React, { useEffect, useRef, useState } from 'react';

import { Box } from '@mui/material';
import * as Sentry from '@sentry/react';
import PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';
import { connect, useDispatch, useSelector } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { compose } from 'redux';
import { createStructuredSelector } from 'reselect';
import SparkMD5 from 'spark-md5';
import uuid from 'uuid';

import BlockingBannerAccordingToPlan from 'components/Banner/BlockingBannerAccordingToPlan';
import AddItemButton from 'components/PdfViewer/add-items-controls/AddItemButton';
import usePdfTronViewer from 'components/PdfViewer/usePdfTronViewer';
import {
  buildUpdateDocumentPayload,
  buildUpdatePlanPayload,
  createPinStampImageData,
  loadDocumentBlobFromUrl,
} from 'components/PdfViewer/utils';
import addGlobalReducer from 'containers/GlobalWrapper/reducer';
import { selectLoggedUserData, selectModules } from 'containers/GlobalWrapper/selectors';
import { setGlobalMessage } from 'containers/MessageHandler/actions';
import { updateDocument } from 'containers/OneProjectPage/DocumentsPage/actions';
import oneDocumentsPageReducer from 'containers/OneProjectPage/DocumentsPage/reducer';
import documentsPageSaga from 'containers/OneProjectPage/DocumentsPage/saga';
import { updatePlan } from 'containers/OneProjectPage/PlansPage/actions';
import plansPageReducer from 'containers/OneProjectPage/PlansPage/reducer';
import plansPageSaga from 'containers/OneProjectPage/PlansPage/saga';
import { useFeatureFlags } from 'contexts/FeatureFlagsProvider';
import { Routing } from 'routing/routing';
import globalMessages from 'translations/messages/global-messages';
import Analytics from 'utils/Analytics';
import { generateChunks } from 'utils/chunkUploader';
import { fileChunkSize, MODULE_TYPES } from 'utils/constants';
import injectReducer from 'utils/injectReducer';
import injectSaga from 'utils/injectSaga';
import { reportPdftronException } from 'utils/pdftron.utils';

// Exports the current annotations and returns the resulting PDF blob.
const exportDocumentAsBlob = async instance => {
  const { documentViewer, annotationManager } = instance.Core;
  documentViewer.setToolMode(documentViewer.getTool(instance.Core.Tools.ToolNames.PAN));
  const doc = documentViewer.getDocument();
  const xfdfString = await annotationManager.exportAnnotations({ useDisplayAuthor: true });
  const data = await doc.getFileData({ xfdfString });
  return new Blob([new Uint8Array(data)], { type: 'application/pdf' });
};

const PdfTronViewerFull = ({
  userInfo,
  isFirstPageOnly = true,
  pdfDocument,
  tools,
  history,
  intl,
  hasOfferLimitation,
  seeAnnotations,
  plan,
  moduleId,
  updatePin,
  entityType,
  changeLoader,
  onPageLoadSuccess,
  onPageLoadError,
  match,
  addZoomButton = true,
  docUrl,
  pins = [],
}) => {
  const { params } = match;
  const { organizationId, idData } = params;

  const featureFlags = useFeatureFlags();
  const shouldAddItems = featureFlags?.plan_add_items;

  const dispatch = useDispatch();
  const modules = useSelector(selectModules());

  const [tronId] = useState(uuid.v4());
  const [annotationSelected, setAnnotationSelected] = useState();
  const [isClickToPlacePinMode, setIsClickToPlacePinMode] = useState(false);
  const [triggerClickToPlacePin, setTriggerClickToPlacePin] = useState({ x: 0, y: 0 });
  const [isAnnotating, setIsAnnotating] = useState(false);
  const [zoomUpdated, setZoomUpdated] = useState(false);
  const [needUpdateAnnotations, setNeedUpdateAnnotations] = useState(false);

  const mountRef = useRef(null);

  // tthe WebViewer initialisation effect runs only once on mount,
  // so PDFTron's imperative callbacks would otherwise close over stale props.
  // We mirror every prop/handler they consume inside this ref and refresh it
  // on every render to keep them in sync without re-initialising the viewer.
  const latestPropsRef = useRef({});

  const dispatchUpdatePlan = (blob, spark2, uuidBlobFile) => {
    dispatch(
      updatePlan(buildUpdatePlanPayload(plan, blob, spark2, uuidBlobFile), () => {
        Analytics.track('plans_tools_save');
        dispatch(
          setGlobalMessage({
            type: 'success',
            intlMessage: { id: 'update_successful' },
            verticalPosition: 'bottom',
            horizontalPosition: 'left',
          }),
        );
      }),
    );
  };

  const dispatchUpdateDocument = (blob, spark2, uuidBlobFile) => {
    dispatch(updateDocument(buildUpdateDocumentPayload(pdfDocument, blob, spark2, uuidBlobFile)));
  };

  latestPropsRef.current = {
    hasOfferLimitation,
    history,
    organizationId,
    isFirstPageOnly,
    changeLoader,
    onPageLoadSuccess,
    onPageLoadError,
    tools,
    plan,
    pdfDocument,
    dispatchUpdatePlan,
    dispatchUpdateDocument,
  };

  const handleSaveDocument = async ({ instance, disableSaveButton }) => {
    const {
      hasOfferLimitation: limitationActive,
      history: latestHistory,
      organizationId: latestOrganizationId,
      plan: latestPlan,
      pdfDocument: latestPdfDocument,
      dispatchUpdatePlan: latestDispatchUpdatePlan,
      dispatchUpdateDocument: latestDispatchUpdateDocument,
    } = latestPropsRef.current;

    if (limitationActive) {
      latestHistory.push(Routing.organizations.contract(latestOrganizationId));
      return;
    }

    const blob = await exportDocumentAsBlob(instance);
    const spark2 = new SparkMD5.ArrayBuffer();
    spark2.append(await blob.arrayBuffer());

    const persist = uuidBlobFile => {
      if (latestPlan) {
        latestDispatchUpdatePlan(blob, spark2, uuidBlobFile);
      } else if (latestPdfDocument) {
        latestDispatchUpdateDocument(blob, spark2, uuidBlobFile);
      }
      disableSaveButton();
    };

    if (blob.size > fileChunkSize) {
      const fileName = latestPlan ? latestPlan?.name : latestPdfDocument?.name;
      const file = new File([blob], fileName, { type: blob.type });
      generateChunks({
        file,
        filesUpload: [],
        singleFileSelection: true,
        filesToUpload: [blob],
        setFiles: files => persist(files[0].uuidBlobFile),
      });
    } else {
      persist();
    }
  };

  const { pdfTronInstance, isClickToPlacePinModeRef } = usePdfTronViewer({
    mountRef,
    intl,
    tronId,
    docUrl,
    addZoomButton,
    pdfDocument,
    userInfo,
    initialTools: tools,
    latestPropsRef,
    setters: {
      setAnnotationSelected,
      setIsAnnotating,
      setZoomUpdated,
      setNeedUpdateAnnotations,
      setTriggerClickToPlacePin,
    },
    onSaveDocument: handleSaveDocument,
  });

  useEffect(
    () => {
      isClickToPlacePinModeRef.current = isClickToPlacePinMode;
      const pdftronCore = pdfTronInstance?.Core;
      const pdftronUI = pdfTronInstance?.UI;
      if (pdftronCore && pdftronUI) {
        if (isClickToPlacePinMode) {
          pdftronCore.annotationManager.enableReadOnlyMode();
        } else {
          const { documentViewer } = pdfTronInstance.Core;
          pdftronCore.annotationManager.disableReadOnlyMode();
          const panTool = documentViewer.getTool(pdfTronInstance.Core.Tools.ToolNames.PAN);
          documentViewer.setToolMode(panTool);
        }
      }
    },
    [isClickToPlacePinMode, pdfTronInstance?.UI],
  );

  const updateMousePin = () => {
    if (!pdfTronInstance) return;
    const { annotationManager, documentViewer } = pdfTronInstance.Core;
    const viewerEl = documentViewer.getViewerElement();
    const pinContainer = viewerEl.getElementsByClassName('pins-container');
    const allAnnots = annotationManager.getAnnotationsList();

    const pinSvgEncoded = `data:image/svg+xml,${encodeURIComponent(
      createPinStampImageData({
        pin: { module_id: moduleId },
        modules,
        withCrossHair: true,
      }).outerHTML,
    )}`;

    viewerEl.style.cursor = isClickToPlacePinMode
      ? `url("${pinSvgEncoded}") 30.5 72, crosshair`
      : 'default';

    if (pinContainer.length) {
      pinContainer[0].style.display = isClickToPlacePinMode ? 'none' : 'block';
    }

    /* eslint no-param-reassign: "error" */
    allAnnots.forEach(annot => {
      annot.Listable = !isClickToPlacePinMode;
    });

    annotationManager.drawAnnotationsFromList(allAnnots);
  };

  useEffect(
    () => {
      if (!pdfTronInstance) return;
      const { documentViewer } = pdfTronInstance.Core;
      const viewerEl = documentViewer.getViewerElement();

      const div = document.createElement('div');
      div.setAttribute('class', 'pins-container');
      div.setAttribute(
        'style',
        'position: absolute; top: 0; left: 0; width: 100%; height: 100%; z-index:999',
      );
      const existingPinsDiv = viewerEl.getElementsByClassName('pins-container');
      if (existingPinsDiv.length) existingPinsDiv[0].remove();

      const virtualListContainer = viewerEl.querySelector('#virtualListBody');
      if (virtualListContainer) {
        if (isClickToPlacePinMode) {
          viewerEl.appendChild(div);
        } else {
          virtualListContainer.appendChild(div);
        }
      }

      pins.forEach(pin => {
        const itemPin = createPinStampImageData({ pin, modules, withCrossHair: false });
        itemPin.setAttribute(
          'style',
          `position: absolute; left: calc(${100 * pin?.position_x}% - ${30.5}px); top: calc(${100 *
            pin?.position_y}% - ${60}px); cursor: pointer;`,
        );
        div.appendChild(itemPin);

        itemPin.addEventListener('click', () => {
          window.open(
            entityType === MODULE_TYPES.observations
              ? Routing.observations.detail(organizationId, idData, pin.module_id, pin.id)
              : Routing.forms.detail(organizationId, idData, pin.module_id, pin.id),
            '_blank',
          );
        });
      });

      updateMousePin();
      setZoomUpdated(false);
    },
    [pins, pdfTronInstance, zoomUpdated, needUpdateAnnotations],
  );

  useEffect(
    () => {
      updateMousePin();
    },
    [isClickToPlacePinMode],
  );

  useEffect(
    () => {
      if (!pdfTronInstance) return;
      const { annotationManager } = pdfTronInstance.Core;
      const allAnnots = annotationManager.getAnnotationsList();
      if (seeAnnotations) annotationManager.showAnnotations(allAnnots);
      else annotationManager.hideAnnotations(allAnnots);
    },
    [seeAnnotations, pdfTronInstance],
  );

  useEffect(
    () => {
      if (!pdfTronInstance || !docUrl) return;
      (async () => {
        try {
          if (changeLoader) changeLoader(true);
          await loadDocumentBlobFromUrl({
            url: docUrl,
            loadDocument: (blob, options) => pdfTronInstance.UI.loadDocument(blob, options),
          });
        } catch (err) {
          if (onPageLoadError) onPageLoadError();
          if (changeLoader) changeLoader(false);

          Sentry.captureException(err, {
            tags: { area: 'PDFTRON', action: docUrl, code: 'FAIL_LOAD_PLAN_DOCUMENT' },
            extra: { tron_id: tronId, path: window.location?.pathname },
          });
          reportPdftronException(err, 'FULL_DOCUMENT_FETCH_OR_LOAD_FAILED', {
            tron_id: tronId,
            doc_url: docUrl,
          });
        }
      })();
    },
    [pdfTronInstance, docUrl],
  );

  return (
    <>
      <div
        ref={mountRef}
        key={`scroll-view-${tronId}`}
        id={`scroll-view-${tronId}`}
        style={{
          width: '100%',
          height: '100%',
          overflow: 'hidden',
          position: 'absolute',
        }}
      />
      {hasOfferLimitation &&
        annotationSelected &&
        !isClickToPlacePinMode && (
          <Box sx={{ margin: '8px 16px', position: 'absolute', bottom: '45px' }}>
            <BlockingBannerAccordingToPlan
              title={intl.formatMessage(globalMessages.offers_custom_modules_banner_title)}
              description={intl.formatMessage(
                globalMessages.offers_business_plan_annotation_description,
              )}
            />
          </Box>
        )}
      <div id="clickToPlacePin">
        {Boolean(shouldAddItems) &&
          pdfTronInstance?.Core?.documentViewer && (
            <AddItemButton
              triggerClickToPlacePin={triggerClickToPlacePin}
              entityType={entityType}
              moduleId={moduleId}
              updatePin={() => {
                const { documentViewer } = pdfTronInstance.Core;
                documentViewer.setToolMode(
                  documentViewer.getTool(pdfTronInstance.Core.Tools.ToolNames.PAN),
                );
                setIsClickToPlacePinMode(false);
                updatePin();
              }}
              isPinsVisible={placing => setIsClickToPlacePinMode(!placing)}
              showCustomPin={() => setIsClickToPlacePinMode(true)}
              hideCustomPin={() => setIsClickToPlacePinMode(false)}
              isViewerFull
              isAnnotating={isAnnotating}
            />
          )}
      </div>
    </>
  );
};

PdfTronViewerFull.propTypes = {
  pins: PropTypes.arrayOf(PropTypes.object),
  docUrl: PropTypes.string,
  onPageLoadSuccess: PropTypes.func,
  onPageLoadError: PropTypes.func,
  match: PropTypes.object,
  entityType: PropTypes.string,
  addZoomButton: PropTypes.bool,
  changeLoader: PropTypes.func,
  updatePin: PropTypes.func,
  moduleId: PropTypes.string,
  plan: PropTypes.object,
  seeAnnotations: PropTypes.bool,
  hasOfferLimitation: PropTypes.bool,
  intl: PropTypes.object.isRequired,
  history: PropTypes.object.isRequired,
  pdfDocument: PropTypes.object,
  tools: PropTypes.shape({
    annotationVisibilityOptionEnabled: PropTypes.bool,
    itemsVisibilityOptionEnabled: PropTypes.bool,
    annotationAreVisible: PropTypes.bool,
    itemsAreVisible: PropTypes.bool,
    toggleAnnotationVisibility: PropTypes.func,
    toggleItemsVisibility: PropTypes.func,
  }),
  isFirstPageOnly: PropTypes.bool,
  userInfo: PropTypes.object,
};

const mapDispatchToProps = {
  dispatchSetGlobalMessage: setGlobalMessage,
};

const mapStateToProps = createStructuredSelector({
  userInfo: selectLoggedUserData(),
});

const withConnect = connect(
  mapStateToProps,
  mapDispatchToProps,
);

const withSaga = injectSaga({ key: 'plansPage', saga: plansPageSaga });
const withReducer = injectReducer({ key: 'plansPage', reducer: plansPageReducer });
const withReducerGlobal = injectReducer({ key: 'global', reducer: addGlobalReducer });

const withSagaDocument = injectSaga({ key: 'documentsPage', saga: documentsPageSaga });
const withReducerDocument = injectReducer({
  key: 'documentsPage',
  reducer: oneDocumentsPageReducer,
});

export default compose(
  withSaga,
  withReducer,
  withSagaDocument,
  withReducerDocument,
  withReducerGlobal,
  withRouter,
  withConnect,
  injectIntl,
)(PdfTronViewerFull);
